import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export default function TutorialModal({ isFacilitator, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const [shouldShow, setShouldShow] = useState(true);

  const storageKey = isFacilitator
    ? 'retro_tutorial_facilitator_seen'
    : 'retro_tutorial_participant_seen';

  const facilitatorSteps = [
    {
      title: 'Welcome, Facilitator! 👋',
      body: "You're in charge. You control the phases and reveal cards when ready."
    },
    {
      title: 'Choose a Format 📋',
      body: 'Different formats suit different situations. Classic Start/Stop/Continue works for most sprints.'
    },
    {
      title: 'Writing Phase ✍️',
      body: 'Team members add cards. Cards are hidden until you reveal them.'
    },
    {
      title: 'Reveal & Group 👁️',
      body: 'Hit Reveal to show cards. Drag similar cards together to create groups.'
    },
    {
      title: 'Discussion & Analysis 💬',
      body: 'Talk through the feedback. Generate an AI analysis to summarise for reports.'
    },
    {
      title: 'Voting ✅',
      body: 'Team votes on action items. Top voted items rise to the top.'
    },
    {
      title: 'Export & Share 📤',
      body: 'Download a PDF report or post the summary to Slack.'
    }
  ];

  const participantSteps = [
    {
      title: 'Welcome to the Retro! 👋',
      body: 'Share honestly — cards are hidden until the facilitator reveals them.'
    },
    {
      title: 'Add Your Cards ✍️',
      body: 'Click the + button next to each phase to add your feedback.'
    },
    {
      title: 'Vote on Actions ✅',
      body: 'During voting, pick the action items you think matter most.'
    }
  ];

  const steps = isFacilitator ? facilitatorSteps : participantSteps;

  useEffect(() => {
    if (localStorage.getItem(storageKey)) {
      setShouldShow(false);
    }
  }, [storageKey]);

  const handleClose = () => {
    setShouldShow(false);
    if (dontShowAgain) {
      localStorage.setItem(storageKey, 'true');
    }
    onClose();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!shouldShow) {
    return null;
  }

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px'
    }}>
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '32px',
        width: '100%',
        maxWidth: '480px',
        boxShadow: 'var(--shadow-lg)',
        animation: 'slide-up 0.2s cubic-bezier(0.34,1.2,0.64,1)'
      }}>
        {/* Progress dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '32px'
        }}>
          {steps.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: idx <= currentStep ? 'var(--primary)' : 'var(--border)',
                transition: 'background 0.2s'
              }}
            />
          ))}
        </div>

        {/* Step content */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '1.3rem',
            fontWeight: 700,
            marginBottom: '12px',
            color: 'var(--text)'
          }}>
            {step.title}
          </h2>
          <p style={{
            fontSize: '15px',
            lineHeight: 1.6,
            color: 'var(--text-muted)',
            margin: 0
          }}>
            {step.body}
          </p>
        </div>

        {/* Checkbox */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '24px',
          fontSize: '13px',
          color: 'var(--text-muted)'
        }}>
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            style={{
              width: '16px',
              height: '16px',
              cursor: 'pointer',
              accentColor: 'var(--primary)'
            }}
          />
          <label style={{ cursor: 'pointer' }}>
            Don't show again
          </label>
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: 'var(--radius-sm)',
                background: currentStep === 0 ? 'var(--border)' : 'var(--bg-card)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 0 ? 0.4 : 1,
                transition: 'all 0.15s',
                fontFamily: 'Syne, sans-serif'
              }}
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <button
              onClick={isLastStep ? handleClose : handleNext}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
                fontFamily: 'Syne, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--primary-hover)';
                e.currentTarget.style.boxShadow = 'var(--glow)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--primary)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight size={16} />}
            </button>
          </div>

          {/* Skip link */}
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: '13px',
              cursor: 'pointer',
              padding: 0,
              fontFamily: 'Syne, sans-serif',
              transition: 'color 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            Skip tutorial
          </button>
        </div>
      </div>
    </div>
  );
}
